import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, CurrencyDollar, ArrowClockwise, DownloadSimple, Info, MagicWand, WarningCircle } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { apiError } from "@/lib/api";
import { Field } from "../components/Modal";
import { MediaImage } from "../components/MediaImage";
import { VisualPromptBuilder } from "../components/VisualPromptBuilder";
import { useCreativeData, creativeApi, promptApi, pollJobUntilDone, fetchMediaMap, mediaUrl } from "../useCreative";
import { fmtCost } from "../creativeUtils";

const TERMINAL_OK = "completed";

export default function Generate() {
  const { brandKits, templates } = useCreativeData();
  const location = useLocation();
  const [structured, setStructured] = useState({ subject: "", aspect_ratio: "1:1" });
  const [rendered, setRendered] = useState("");
  const [extras, setExtras] = useState({ brand_kit_id: "", template_id: "" });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { job, assets:[{...,url}] }
  const [copilotText, setCopilotText] = useState("");
  const [composing, setComposing] = useState(false);
  const [parentAssetId, setParentAssetId] = useState(null); // Remix lineage

  const setExtra = (k, v) => setExtras((f) => ({ ...f, [k]: v }));

  // Remix: a source asset routes here with its full recipe pre-loaded into the Builder.
  useEffect(() => {
    const remix = location.state?.remix;
    if (remix?.structured) {
      setStructured({ ...remix.structured });
      if (remix.parent_asset_id) setParentAssetId(remix.parent_asset_id);
      toast.success("Loaded into the Builder — edit any field, then Generate");
      window.history.replaceState({}, ""); // consume once (avoid re-applying on back)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runCopilot = async () => {
    if (!copilotText.trim()) return toast.error("Describe the creative you want");
    setComposing(true);
    try {
      const res = await promptApi.compose(copilotText.trim(), "image");
      setStructured({ ...res.structured });
      toast.success(res.fallback ? "Draft ready — review & refine in the Builder" : "AI Copilot filled the Builder — review & edit");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setComposing(false);
    }
  };

  const applyTemplate = (id) => {
    setExtra("template_id", id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl?.aspect_ratio) setStructured((s) => ({ ...s, aspect_ratio: tpl.aspect_ratio }));
  };

  const buildBrief = () => ({
    kind: "image",
    operation: "generate",
    prompt: rendered.trim(),
    negative_prompt: structured.negative_prompt || null,
    aspect_ratio: structured.aspect_ratio || null,
    style: structured.style || null,
    brand_kit_id: extras.brand_kit_id || null,
    prompt_template: extras.template_id ? (templates.find((t) => t.id === extras.template_id)?.prompt_key || null) : null,
    params: { structured, ...(parentAssetId ? { parent_asset_id: parentAssetId } : {}) },  // canonical Structured Prompt (backend re-renders authoritatively)
  });

  const hasPrompt = () => rendered.trim().length > 0;

  const onPreview = async () => {
    if (!hasPrompt()) return toast.error("Compose a prompt first");
    setPreviewing(true);
    try {
      const data = await creativeApi.preview(buildBrief());
      setPreview(data);
      if (!data.supported) toast.warning("No active provider can handle this request");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const onGenerate = async () => {
    if (!hasPrompt()) return toast.error("Compose a prompt first");
    setGenerating(true);
    setResult(null);
    try {
      let job = await creativeApi.generate(buildBrief(), `gen-${Date.now()}`);
      if (job.status !== TERMINAL_OK && ["queued", "running"].includes(job.status)) {
        job = await pollJobUntilDone(job.id);
      }
      if (job.status !== TERMINAL_OK) {
        toast.error(job.error || `Generation ${job.status}`);
        setResult({ job, assets: [] });
        return;
      }
      const [allAssets, media] = await Promise.all([creativeApi.listAssets(), fetchMediaMap()]);
      const jobAssets = allAssets
        .filter((a) => a.job_id === job.id)
        .map((a) => ({ ...a, url: mediaUrl(media, a.media_asset_id) }));
      setResult({ job, assets: jobAssets });
      toast.success("Creative generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const brandKitOptions = [{ value: "", label: "No brand kit" }, ...brandKits.map((k) => ({ value: k.id, label: k.name + (k.is_default ? " (default)" : "") }))];
  const templateOptions = [{ value: "", label: "No template" }, ...templates.map((t) => ({ value: t.id, label: t.name }))];

  return (
    <div className="space-y-8" data-testid="creative-generate">
      <PromoPageHeader overline="Creative Studio" title="Generate" description="Compose your creative with the Visual Prompt Builder — structured, provider-agnostic, reusable. Rendered by the best available AI provider." />

      {/* AI Copilot — optional accelerator. Fills the Structured Prompt, then the
          Visual Prompt Builder below opens fully populated for manual review/editing.
          It NEVER replaces or bypasses the Builder. */}
      <div className="rounded-lg border border-ai/30 bg-ai/5 p-5" data-testid="ai-copilot">
        <div className="mb-2 flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-ai/15 text-ai"><MagicWand size={16} weight="fill" /></div>
          <div>
            <div className="text-sm font-semibold">AI Copilot <span className="ml-1 rounded-full bg-ai/15 px-1.5 py-0.5 text-[10px] font-medium text-ai">optional</span></div>
            <div className="text-xs text-muted-foreground">Describe your creative — the Copilot fills the Builder for you. You review and edit every field before generating.</div>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={copilotText}
            onChange={(e) => setCopilotText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !composing) runCopilot(); }}
            placeholder='e.g. "A cinematic Apple commercial at sunset" or "A futuristic Lamborghini at night"'
            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-ai"
            data-testid="copilot-input"
          />
          <button
            onClick={runCopilot}
            disabled={composing}
            className="flex items-center justify-center gap-1.5 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            data-testid="copilot-compose"
          >
            {composing ? <ArrowClockwise size={15} className="animate-spin" /> : <MagicWand size={15} weight="fill" />}
            {composing ? "Composing…" : "Compose with AI"}
          </button>
        </div>
      </div>


      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Builder */}
        <div className="space-y-5 rounded-lg border border-border bg-card p-6" data-testid="generate-form">
          <VisualPromptBuilder structured={structured} onChange={setStructured} kind="image" onRenderedChange={setRendered} />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Brand kit">
              <PromoSelect value={extras.brand_kit_id} onChange={(v) => setExtra("brand_kit_id", v)} options={brandKitOptions} placeholder="No brand kit" testId="select-brand-kit" />
            </Field>
            <Field label="Template">
              <PromoSelect value={extras.template_id} onChange={applyTemplate} options={templateOptions} placeholder="No template" testId="select-template" />
            </Field>
          </div>

          {preview && (
            <div className="rounded-md border border-border bg-accent/30 p-4 text-sm" data-testid="preview-result">
              {preview.supported ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="flex items-center gap-1.5 font-medium text-brand"><CurrencyDollar size={15} weight="bold" /> {fmtCost(preview.estimate?.amount, preview.estimate?.currency)}</span>
                  <span className="text-muted-foreground">Provider: <span className="text-foreground">{preview.provider}</span></span>
                  <span className="text-muted-foreground">Capability: <span className="font-mono text-xs">{preview.capability}</span></span>
                </div>
              ) : (
                <span className="flex items-center gap-1.5 text-warning"><Info size={15} weight="fill" /> No active provider supports this request.</span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={onPreview}
              disabled={previewing || generating}
              className="flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
              data-testid="btn-preview"
            >
              <CurrencyDollar size={15} className={previewing ? "animate-pulse" : ""} /> Estimate cost
            </button>
            <button
              onClick={onGenerate}
              disabled={generating}
              className="flex flex-1 items-center justify-center gap-2 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              data-testid="btn-generate"
            >
              {generating ? <ArrowClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
              {generating ? "Generating…" : "Generate"}
            </button>
          </div>
        </div>

        {/* Result */}
        <div className="rounded-lg border border-border bg-card p-6" data-testid="generate-result">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-medium">Result</h3>
            {result?.job && <StatusBadge status={result.job.status} />}
          </div>

          {generating ? (
            <div className="flex aspect-square w-full items-center justify-center rounded-md promo-skeleton" data-testid="result-loading">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Sparkle size={28} weight="duotone" className="animate-pulse text-ai" />
                <span className="text-sm">Rendering your creative…</span>
              </div>
            </div>
          ) : result?.assets?.length ? (
            <div className="space-y-4" data-testid="result-assets">
              {result.assets.map((a) => (
                <div key={a.id} className="overflow-hidden rounded-md border border-border">
                  <MediaImage url={a.url} alt="generated" className="w-full object-contain" />
                  <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground">
                    <span>{a.width && a.height ? `${a.width}×${a.height}` : a.aspect_ratio || "image"} · {a.provider}</span>
                    {a.url && (
                      <a href={a.url} target="_blank" rel="noreferrer" download className="flex items-center gap-1 text-brand hover:underline" data-testid={`download-${a.id}`}>
                        <DownloadSimple size={13} /> Download
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : result?.job && result.job.status !== TERMINAL_OK ? (
            <div className="flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-6 text-center" data-testid="result-error">
              <WarningCircle size={30} weight="fill" className="text-destructive" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-destructive">Generation failed</p>
                <p className="text-xs text-muted-foreground" data-testid="result-error-message">
                  {result.job.error || `The provider did not return an image (status: ${result.job.status}).`}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-background/40 text-center text-muted-foreground" data-testid="result-empty">
              <Sparkle size={28} weight="duotone" />
              <p className="text-sm">Your generated creative will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
